/**
 * Human-readable file size. Used for storage used on Home and Documents progress bar.
 * @param bytes - Size in bytes
 * @param granular - If true, show 2 decimal places (e.g. 1.50 MB); otherwise 1
 * @returns e.g. "0 KB", "0.10 KB", "1.50 MB", "2.3 GB"
 */
export function formatFileSize(bytes: number, granular = false): string {
  if (bytes == null || bytes <= 0) return '0 KB';
  const units = ['KB', 'MB', 'GB', 'TB'];
  let val = bytes;
  let i = -1;
  do {
    val = val / 1024;
    i++;
    // - 1 (not - 2): the old bound made TB unreachable, so a 5 TB account
    // rendered as "5120.0 GB".
  } while (val >= 1024 && i < units.length - 1);
  return `${Math.max(val, 0.1).toFixed(granular ? 2 : 1)} ${units[i]}`;
}

/**
 * THE byte formatter. Seven screens grew private copies that disagreed on
 * screen (three couldn't render GB at all, so a 2 GB file read "2048.0 MB";
 * decimals varied per surface). This is the admin documentsHelpers algorithm,
 * promoted: full B→TB range, whole bytes below 1 KB, one decimal above
 * (naturally trimmed — 2 MB is "2 MB", not "2.0 MB"), "0 B" for empty.
 *
 * formatFileSize above stays for the storage meters that want fixed decimals
 * and a KB floor; everything else uses this.
 */
export function formatBytes(bytes: number | undefined | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  let rounded = i === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  // Rounding happens after the unit pick, so a boundary value can overflow
  // its unit (1,048,575 B ≈ 1023.999 KB → "1024 KB"); roll it into the next
  // tier instead. TB has no next tier and may legitimately exceed 1024.
  if (rounded >= 1024 && i < units.length - 1) {
    i += 1;
    rounded = Math.round((value / 1024) * 10) / 10;
  }
  return `${rounded} ${units[i]}`;
}
